import { ExportStatus } from '@/app/enums';
import { AlpineData, LivewireData, LivewireMethods } from '@/js/interfaces/Components/alpinejs/Estimate/EstimateExporter';
import config from '@/js/config/app.json';
import is from '@/js/app/support/is';
import { InvalidLivewireResponseFormat } from '@/app/exceptions';
import { translate } from '@/js/app/support/utils';
import { createAlpineLivewireComponent } from '@nolikein/typed-alpinejs';
import { notifyInformationAlert } from '@/js/app/support/alert-and-banner';
import { NOTIFICATION_MEDIUM_DURATION } from '@/js/interfaces/Components/alpinejs/NotificationAlert';

const messageStepOne = translate('Step 1: Sending my export request');
// const messageStepTwo = translate('Step 2: Waiting for my request to be processed');
const messageStepThree = translate('Step 2: The treatment of the export related information is in process');

const exportSessionKeyInStorage: string = config.components.EstimateExporter.localStorageKey;

export default {
    name: 'EstimateExporter',
    component: (
        documentText: string = 'Document',
    ) => createAlpineLivewireComponent<AlpineData, LivewireData, LivewireMethods>({
        exportStatus: ExportStatus.WaitingUserAction,
        exportProgressionMessage: '',
        exportFilename: '',
        isRequestingExport: false,
        percentage: 0,

        /**
         * Initialize the component
         */
        async init() {

            // if (this.didAnExportAlreadyStarted()) {
            //     // Try to retrieve the filename of a previous runned export
            //     const exportFilename = this.getStoredExportData()?.filename;
            //     if (undefined === exportFilename || null === exportFilename || '' === exportFilename) {
            //         this.cleanExportStorage();
            //         throw 'The export filename was not found in the local storage. Perhaps the storage is corrupted, it will be reset.';
            //     } else {
            //         // Change the status to show the waiting display
            //         this.exportStatus = ExportStatus.GeneratingFile;

            //         // Set the filename property of the livewire component (this affect the openned link when the file is fully generated)
            //         await this.$wire.set('exportFilename', exportFilename);
            //         this.exportFilename = exportFilename;
            //         this.checkExportStatus();
            //     }
            // }
        },

        /**
         * Trigger export to excel with custom filters registered in the current livewire component.
         * If any excel sheet check fails, the livewire component/view will manage that.
         */
        exportToExcel() {
            this.isRequestingExport = true;
            this.exportProgressionMessage = '';

            notifyInformationAlert(messageStepOne, 2000);

            this.$wire.$call('exportContentToExcel')
                .then((data) => {
                    this.isRequestingExport = false;

                    // Check filename existence (string)
                    if (!('filename' in data)) {
                        return;
                    }
                    if (!is.string(data.filename)) {
                        throw new InvalidLivewireResponseFormat('The Livewire response body should have a property "filename" of type string');
                    }

                    // Register the export filename both in the component and in the local storage
                    this.exportFilename = data.filename;

                    this.storeExportData({
                        filename: this.exportFilename,
                    });

                    // Change the status to show the waiting display
                    this.exportStatus = ExportStatus.Done;
                    // Change the message after a few time to let the user read messages
                    setTimeout(() => {
                        notifyInformationAlert(messageStepThree, NOTIFICATION_MEDIUM_DURATION);
                    }, 500);
                    // Check the export state only one second after
                    // setTimeout(() => { this.checkExportStatus(); }, 1000);
                })
                .catch(() => {
                    this.isRequestingExport = false;
                });
        },

        /**
         * Check any 1 second the export is finished.
         * @deprecated
         */
        async checkExportStatus() {
            const data = await this.$wire.$call('checkExportStatus', this.exportFilename);

            // Check treatment_began existence (string)
            if (!('treatment_began' in data) || !is.string(data.treatment_began)) {
                throw new InvalidLivewireResponseFormat('The Livewire response body should have a property "treatment_began" of type string');
            }
            // Check other fields
            if (!('sheet_current_index' in data) || !is.number(data.sheet_current_index)) {
                throw new InvalidLivewireResponseFormat('The Livewire response body should have a property "sheet_current_index" of type number');
            }
            if (!('sheet_total_quantity' in data) || !is.number(data.sheet_total_quantity)) {
                throw new InvalidLivewireResponseFormat('The Livewire response body should have a property "sheet_total_quantity" of type number');
            }
            if (!('sheet_current_name' in data) || !is.string(data.sheet_current_name)) {
                throw new InvalidLivewireResponseFormat('The Livewire response body should have a property "sheet_current_name" of type string');
            }
            if (!('percentage' in data) || !is.number(data.percentage)) {
                throw new InvalidLivewireResponseFormat('The Livewire response body should have a property "percentage" of type number');
            }
            if (!('should_ask_later' in data) || !is.boolean(data.should_ask_later)) {
                throw new InvalidLivewireResponseFormat('The Livewire response body should have a property "should_ask_later" of type boolean');
            }

            // If the export treatment has begun, we update the user message to show treatment advancement
            if (data.treatment_began) {
                notifyInformationAlert(messageStepThree, 2000);
                this.exportProgressionMessage = `${documentText} ${data.sheet_current_index}/${data.sheet_total_quantity} : ${data.sheet_current_name} ( ${data.percentage} %)`;
                this.percentage = data.percentage;
            }

            if (data.should_ask_later) {
                // Rerun the export checking method in a few seconds
                setTimeout(() => {
                    this.checkExportStatus();
                }, 2000);
            } else {
                // Set the export status to DONE then download the file automatically
                this.exportStatus = ExportStatus.Done;
                const el = document.getElementById('excel-export-url');
                if (null === el) {
                    throw new Error('The id "excel-export-url" is not defined');
                }
                el.click();
                // Remove the export data from the local storage
                this.cleanExportStorage();
            }
        },

        /**
         * Checks the export has already started previously
         */
        didAnExportAlreadyStarted(): boolean {
            return null !== this.storage().getItem(exportSessionKeyInStorage);
        },

        /**
         * Retrieve data from the previsouly started export
         */
        getStoredExportData(): unknown {
            const storedContent = this.storage().getItem(exportSessionKeyInStorage);
            if (null === storedContent) {
                throw new Error(`You cannot access the stored item with key "${exportSessionKeyInStorage}" before it is defined`);
            }
            return JSON.parse(storedContent);
        },

        /**
         * Store data about an export
         * @param data Export data
         */
        storeExportData(data: unknown) {
            this.storage().setItem(exportSessionKeyInStorage, JSON.stringify(data));
        },

        /**
         * Clean any data about export that previously started
         */
        cleanExportStorage() {
            this.storage().removeItem(exportSessionKeyInStorage);
        },

        /**
         * Tells the frontend to stop following the current export
         */
        stopFollowingExport() {
            this.exportStatus = ExportStatus.WaitingUserAction;
            this.exportFilename = '';
            this.cleanExportStorage();
        },

        /**
         * Get access to the storage (in this case, localStorage)
         */
        storage(): Storage {
            return window.localStorage;
        },

        /**
         * Check the export status to be done.
         * @returns
         */
        isDone(): boolean {
            return this.exportStatus === ExportStatus.Done;
        },

        /**
         * Check the export status to have an error.
         * @returns
         */
        gotAnError(): boolean {
            return this.exportStatus === ExportStatus.GotAnError;
        },
    }),
};
